import React, { useState, useEffect } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import styled from 'styled-components'
import { connect } from 'react-redux'

//Components
import { SectionLoader } from '../../../_components/SectionLoader'
import { EmptyMessage } from '../../../_components/EmptyMessage'
import { Button } from '../../../_components/Button'
import { ButtonRow } from '../../../_components/layout/ButtonRow'
import { ChargebackModal } from './ChargebackModal'
import { AccrueFeesModal } from './AccrueFeesModal'
import { InvoiceSummary, S_InvoiceSummary } from './InvoiceSummary'
import { StatusBanner, S_StatusBanner } from '../../../_components/StatusBanner'
import { VerificationNoteEditor } from '../../../_components/Notes/VerificationNoteEditor'
import { VerificationNoteList, S_VerificationNoteList } from '../../../_components/Notes/VerificationNoteList'

//Services
import { businessService } from '../../../_services/business.service'
import { getInvoice, createInvoiceVerification, uploadVerificationDoc } from '../../../_services/lender.service'
import { getDocument } from '../../../_services/lender.service'
import { getDebtor } from '../../../_services/debtor.service'

import { useFlags } from '../../../_reduxHooks/flags.hook'

//Actions
import { modalActions, notificationsActions } from '../../../_actions'

//Helpers
import { logError } from '../../../_helpers/errors'
import { isFeatureEnabled } from '../../../_helpers/flags'

export const S_InvoiceDetails = styled.div`
  min-width: 850px;
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .edit-button-style {
    display: flex;
    flex-direction: row;
  }

  .icon {
    margin-right: 5px;
  }
  ${S_StatusBanner} {
    margin: 5px;
    max-height: 41px;
  }
  .invoice-verification-edit {
    justify-content: space-between;
    min-height: 250px;
  }
  .verification-note-controls {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-basis: 0;
    margin-top: 32px;
  }
  ${S_VerificationNoteList} {
    flex-basis: 0;
    .note-list {
      height: 100%;
      max-height: 100%;
      overflow-y: scroll;
    }
    .note-list::-webkit-scrollbar {
      display: none;
    }
  }
  ${S_InvoiceSummary} {
  }
  .detail-container {
    display: flex;
  }
`
const InvoiceDetailsModal = ({
  chargebackErrorNotify,
  chargebackSuccessNotify,
  stopFeesSuccessNotify,
  stopFeeErrorNotify,
  closeModal,
  Modal,
  invoice_id,
  onAddVerificationNote = () => {},
  openModal,
  tenant_id,
}) => {
  const [businessName, setBusinessName] = useState(null)
  const [creatingVerification, setCreatingVerification] = useState(false)
  const [debtorId, setDebtorId] = useState(null)
  const [debtorName, setDebtorName] = useState('')
  const [document, setDocument] = useState(null)
  const [edit, setEdit] = useState(false)
  const [error, setError] = useState(null)
  const [invoice, setInvoice] = useState(null)
  const [showSummaryDetails, setShowSummaryDetails] = useState(false)
  const handleShowSummaryDetails = () => setShowSummaryDetails((prev) => !prev)
  const [accrueFees, setAccrueFees] = useState(false)

  const [isLoading, setIsLoading] = useState(true)

  const { featureFlags } = useFlags()

  const getInvoiceData = () => {
    setIsLoading(true)
    getInvoice(invoice_id).then(
      (resp) => {
        const { debtor_id, business_id } = resp

        setDebtorId(() => debtor_id)
        unstable_batchedUpdates(() => {
          setInvoice({
            ...resp,
            verifications: resp.verifications.sort((a, b) => b.ordinal - a.ordinal),
          })

          if (business_id) {
            businessService.getBusiness(business_id).then((resp) => {
              setBusinessName(resp.business_name)
              setIsLoading(false)
            }, logError)
          } else {
            setIsLoading(false)
          }
        })

        if (resp.document_id) {
          getDocument(resp.document_id).then(async (resp) => {
            setDocument(resp)
            setIsLoading(false)
          }, logError)
        }

        if (debtor_id) {
          getDebtor(debtor_id).then((resp) => {
            setDebtorName(resp.debtor_name)
          }, logError)
        }
      },
      () => {
        setError('Error loading invoice.')
      }
    )
  }

  // Initial call to get invoice
  useEffect(() => {
    if (!isLoading) return
    getInvoiceData()
  }, [])

  const refreshData = () => {
    return getInvoiceData()
  }

  const saveVerificationNote = async ({ type, text, files, contact: contactId }) => {
    setCreatingVerification(true)
    let createdVerification = await createInvoiceVerification(invoice_id, type, contactId, text)
    for (let i = 0; i < files.length; i++) {
      await uploadVerificationDoc(createdVerification.id, files[i])
    }
    setCreatingVerification(false)
    refreshData()
    onAddVerificationNote()
  }

  const alreadyStopFees = invoice?.fees_cutoff_date ? true : false
  const isStopInvoiceFeesEnabled = isFeatureEnabled('stop_invoice_fees', featureFlags)
  const showStopFeesButton = isStopInvoiceFeesEnabled && invoice?.state === 'Purchased' && !alreadyStopFees

  return (
    <S_InvoiceDetails>
      {isLoading ? (
        <SectionLoader />
      ) : error ? (
        <EmptyMessage message={error} />
      ) : (
        <>
          <header className="page-header">
            <div>
              <h1>{'Invoice Details'}</h1>
            </div>

            <ButtonRow align="center">
              {invoice.ineligible && (
                <StatusBanner
                  className="warning"
                  icon="warningtriangle"
                  message={`Invoice has been marked ineligible.`}
                />
              )}
              {invoice.fees_cutoff_date && (
                <StatusBanner
                  className="warning"
                  icon="warningtriangle"
                  message={`Invoice has been set to stop accruing fees as of ${invoice.fees_cutoff_date}`}
                />
              )}
              {['Purchased', 'Funded', 'Collected'].indexOf(invoice.state) >= 0 && (
                <Button
                  className={`refund-button`}
                  onClick={() => {
                    openModal(ChargebackModal, {
                      closeModal: closeModal,
                      invoice: invoice,
                      chargebackSuccessNotify: chargebackSuccessNotify,
                      chargebackErrorNotify: chargebackErrorNotify,
                      refreshData: refreshData,
                    })
                  }}
                  text={'Chargeback'}
                />
              )}
              {showStopFeesButton && (
                <Button
                  className={`refund-button`}
                  onClick={() => {
                    openModal(AccrueFeesModal, {
                      closeModal: closeModal,
                      invoice: invoice,
                      stopFeesSuccessNotify: stopFeesSuccessNotify,
                      stopFeeErrorNotify: stopFeeErrorNotify,
                      refreshData: refreshData,
                    })
                  }}
                  text={'Stop Accruing Fees'}
                />
              )}
            </ButtonRow>
          </header>
          <div className={'detail-container'}>
            <div className="verification-note-controls">
              <InvoiceSummary
                debtor_name={debtorName}
                debtorId={debtorId}
                invoice={invoice}
                label={'Create Note'}
                layout={'smooth-tiles'}
              />

              <VerificationNoteEditor
                businessName={businessName}
                callFromModal
                className={'invoice-verification-edit'}
                creatingVerification={creatingVerification}
                debtorId={debtorId}
                flexType={`column`}
                label="Add Verification Note"
                previousModalProps={{ invoice_id, onAddVerificationNote, Modal }}
                save={saveVerificationNote}
                tenant_id={tenant_id}
              />
            </div>
            {invoice && <VerificationNoteList label="Verifications" verifications={invoice.verifications} />}
          </div>
        </>
      )}
    </S_InvoiceDetails>
  )
}

const mapDispatchToProps = (dispatch) => {
  return {
    closeModal: () => {
      dispatch(modalActions.closeModal())
    },
    openModal: (content, props) => {
      dispatch(modalActions.openModal(content, props))
    },
    chargebackSuccessNotify: () => {
      dispatch(
        notificationsActions.showNotification({
          type: `success`,
          message: `Chargeback Successful`,
          id: `chargeback-success`,
        })
      )
    },
    chargebackErrorNotify: () => {
      dispatch(
        notificationsActions.showNotification({
          type: `error`,
          message: `Error during chargeback`,
          id: `chargeback-error`,
        })
      )
    },
    stopFeesSuccessNotify: () => {
      dispatch(
        notificationsActions.showNotification({
          type: `success`,
          message: `Fees Accrued stopped successfully`,
          id: `stop-fees-success`,
        })
      )
    },
    stopFeeErrorNotify: () => {
      dispatch(
        notificationsActions.showNotification({
          type: `error`,
          message: `Error during stop fees`,
          id: `stop-fees-error`,
        })
      )
    },
  }
}

const connectedInvoiceDetails = connect(
  ({ env }) => ({ tenant_id: env.tenant }),
  mapDispatchToProps
)(InvoiceDetailsModal)

export { connectedInvoiceDetails as InvoiceDetailsModal }
